/**
 * Risk-Free Guarantee Banner Component
 * Displays guarantee/policy information with icon
 */

'use client';

import styled from '@emotion/styled';
import { useTheme } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';

export interface RiskFreeGuaranteeProps {
  titleOverride?: string;
  descriptionOverride?: string;
  iconURL?: string;
  backgroundColor?: string;
  borderColor?: string;
}

const Container = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space.xl} ${theme.space.md}`};
`;

const Banner = styled.div<{ bgColor?: string; borderCol?: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.lg};
  padding: ${({ theme }) => theme.space.lg};
  background-color: ${({ theme, bgColor }) => bgColor || theme.colors.background.secondary};
  border: 2px solid ${({ theme, borderCol }) => borderCol || theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    flex-direction: column;
    text-align: center;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    width: 64px;
    height: 64px;
  }
`;

const IconImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    width: 64px;
    height: 64px;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const Title = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;

  strong {
    font-weight: ${({ theme }) => theme.typography.config.weight.heavy};
  }
`;

const Description = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;

  strong {
    font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  }
`;

export function RiskFreeGuarantee({
  titleOverride,
  descriptionOverride,
  iconURL,
  backgroundColor,
  borderColor,
}: RiskFreeGuaranteeProps) {
  const theme = useTheme();
  const { t } = useTranslation('pdp');

  const title = titleOverride || t('riskFree.title');
  const description = descriptionOverride || t('riskFree.description');

  return (
    <Container>
      <Banner bgColor={backgroundColor} borderCol={borderColor}>
        {iconURL ? (
          <IconWrapper>
            <IconImage
              alt={t('riskFree.iconAlt')}
              src={iconURL}
            />
          </IconWrapper>
        ) : (
          <IconWrapper>
            <svg
              fill="none"
              height="64"
              stroke={theme.colors.semantic?.positive?.color || '#22C55E'}
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="64"
            >
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </IconWrapper>
        )}
        <ContentWrapper>
          <Title
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <Description
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </ContentWrapper>
      </Banner>
    </Container>
  );
}
